import { cloneDeep, get, isEmpty, set } from 'lodash';
import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import {
  ExtendedFinanceType,
  FeeCategory,
  FinanceType,
  SpecialFees,
} from '@upstart/harmony/lib/legos/constants';
import { Offer } from '@upstart/harmony/lib/legos/paymentTypes';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import {
  DealerFee,
  FeesByCategory,
} from '@upstart/harmony/lib/legos/dealerTypes';
import * as changeLogHelper from '@upstart/harmony/lib/deskingoffer-changelog';
import { Action, ModalType } from 'util/amplitudeEvents';
import {
  isMarketScanIncentivesEnabled,
  isMarketScanPaymentsEnabled,
} from '@upstart/harmony/lib/dealer-utils';
import DealerFeesModal from '../../PencilEditor/DealerFeesModal';
import EditIncentives from '../../PencilEditor/PencilOfferList/EditIncentives';
import OfferTaxModal from '../OfferTaxModal';
import AddOnsModal from '../AddOnsModal';
import AccessoriesModal from '../AccessoriesModal';
import ProtectionPlansModal from '../ProtectionPlansModal';
import { DeskingOfferCardProps } from './types';
import EditMarketscanIncentives from '../../PencilEditor/PencilOfferList/EditMarketscanIncentives';

const isOnePayLease = ({
  deal,
  dealer,
  offer,
}: Pick<DeskingOfferCardProps, 'offer' | 'deal' | 'dealer'>) =>
  offer.data?.financeType === FinanceType.LEASE &&
  dealer?.desking?.enableOnePayLease &&
  deal?.onePayLease;

const getFeesList = (
  props: Pick<DeskingOfferCardProps, 'offer' | 'deal' | 'dealer'>,
): FeesByCategory => {
  if (
    isOnePayLease(props) &&
    props.dealer?.desking?.differentOnePayFeesFromLease
  ) {
    return get(
      props.offer,
      [
        'data',
        'fees',
        props.offer.data?.condition ?? 'New',
        ExtendedFinanceType.ONE_PAY_LEASE,
      ],
      {},
    );
  }
  return get(
    props.offer,
    [
      'data',
      'fees',
      props.offer.data?.condition ?? 'New',
      props.offer.data?.financeType!,
    ],
    {},
  );
};

const updateFees = ({
  feesByCategory,
  feesFromPayment,
  feeType,
  feeCategory,
}: {
  feesByCategory: FeesByCategory;
  feesFromPayment: DealerFee[];
  feeType: SpecialFees;
  feeCategory: FeeCategory;
}) => {
  const feeFromPayment = feesFromPayment.find(fee => fee.type === feeType);
  if (feeFromPayment) {
    const nontaxableFees = feesByCategory?.[feeCategory] || [];
    const feeInOffer = nontaxableFees.find(fee => fee.type === feeType);
    // update if necessary
    if (feeInOffer && feeInOffer.value !== feeFromPayment.value) {
      feeInOffer.value = feeFromPayment.value;
    }
  }
};

const applyFeesFromPayment = ({
  feesByCategory,
  feesFromPayment,
  financeType,
}: {
  feesByCategory?: FeesByCategory;
  feesFromPayment?: DealerFee[];
  financeType?: FinanceType;
}) => {
  if (!feesByCategory || !feesFromPayment) {
    return;
  }
  // apply florida doc stamp fee
  if (financeType === FinanceType.PURCHASE) {
    updateFees({
      feesByCategory,
      feesFromPayment,
      feeType: SpecialFees.FLORIDA_DOC_STAMP,
      feeCategory: FeeCategory.NON_TAXABLE,
    });
  }

  updateFees({
    feesByCategory,
    feesFromPayment,
    feeType: SpecialFees.OREGON_PRIVILEGE_TAX,
    feeCategory: FeeCategory.NON_TAXABLE,
  });

  updateFees({
    feesByCategory,
    feesFromPayment,
    feeType: SpecialFees.OREGON_CORPORATE_ACTIVITY_TAX,
    feeCategory: FeeCategory.NON_TAXABLE,
  });

  updateFees({
    feesByCategory,
    feesFromPayment,
    feeType: SpecialFees.TENNESSEE_BUSINESS_TAX,
    feeCategory: FeeCategory.NON_TAXABLE,
  });

  updateFees({
    feesByCategory,
    feesFromPayment,
    feeType: SpecialFees.TEXAS_VEHICLE_INVENTORY_TAX,
    feeCategory: FeeCategory.NON_TAXABLE,
  });

  updateFees({
    feesByCategory,
    feesFromPayment,
    feeType: SpecialFees.TEXAS_LENDERS_AND_MEMBERS_FEE,
    feeCategory: FeeCategory.NON_TAXABLE,
  });
};

const getFees = (
  props: Pick<
    DeskingOfferCardProps,
    'offer' | 'deal' | 'dealer' | 'offerPayment'
  >,
): FeesByCategory => {
  const feesByCategory = getFeesList(props);
  applyFeesFromPayment({
    feesByCategory,
    feesFromPayment: props.offerPayment?.fees,
    financeType: props.offer.data?.financeType,
  });
  return { ...feesByCategory };
};

const updateFeesList = (
  props: Pick<DeskingOfferCardProps, 'offer' | 'deal' | 'dealer'>,
  newFees: any,
) => {
  const newOffer = cloneDeep(props.offer);
  const newChangeLog = changeLogHelper.updateFees(newOffer, newFees);
  set(newOffer, 'changeLog', newChangeLog);
  if (
    isOnePayLease(props) &&
    props.dealer?.desking?.differentOnePayFeesFromLease
  ) {
    set(
      newOffer,
      [
        'data',
        'fees',
        newOffer.data?.condition ?? 'New',
        ExtendedFinanceType.ONE_PAY_LEASE,
      ],
      newFees,
    );
  } else {
    set(
      newOffer,
      [
        'data',
        'fees',
        newOffer.data?.condition ?? 'New',
        newOffer.data?.financeType!,
      ],
      newFees,
    );
  }
  return newOffer;
};

export type ModalLabelProps = Omit<DeskingOfferCardProps, 'onChange'> & {
  onSave: (offer: DeskingOffer) => void;
  type: ModalType;
  label: string;
  sublabel?: ReactNode;
  tradeInIndex?: number;
  onIncentivesChange?: (consolidatedOffers: any[]) => void;
  readOnlyMode: boolean;
};

const ModalLabel: FunctionComponent<ModalLabelProps> = props => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [changeLog, setChangeLog] = useState(
    props.offer.changeLog ? props.offer.changeLog : {},
  );
  const [fees, setFees] = useState<FeesByCategory>(getFees(props));
  const currentFinanceType = props.offer.data?.financeType;
  const allOffersForCurrentFinanceType = (props.offers ?? []).filter(
    o => o.data?.financeType === currentFinanceType,
  );
  const isMarketscanEnabled = isMarketScanPaymentsEnabled(props.dealer);

  useEffect(() => {
    setFees(getFees(props));
  }, [props.offer, props.offerPayment, props.dealer]);

  let modal = null;
  switch (props.type) {
    case 'accessories': {
      modal = (
        <AccessoriesModal
          car={props.car}
          offer={props.offer}
          dealer={props.dealer}
          onChange={props.onSave}
          isOpen={isModalOpen}
          closeModal={() => setModalOpen(false)}
          sendAmplitudeEvent={props.sendAmplitudeEvent}
          disabled={props.readOnlyMode}
        />
      );
      break;
    }
    case 'addOns': {
      modal = (
        <AddOnsModal
          offer={props.offer}
          dealer={props.dealer}
          disabled={!!props.disabled || props.readOnlyMode}
          onChange={props.onSave}
          isOpen={isModalOpen}
          closeModal={() => setModalOpen(false)}
          deal={props.deal}
          sendAmplitudeEvent={props.sendAmplitudeEvent}
        />
      );
      break;
    }
    case 'dealerFees': {
      modal = isModalOpen ? (
        <DealerFeesModal
          fees={fees}
          pencil={props.pencil} // TODO: This needs to support an offer
          disabled={!!props.disabled || props.readOnlyMode}
          financeType={props.offer.data?.financeType}
          addInceptionFeesToTotalAtStart={
            props.offer.data?.addInceptionFeesToTotalAtStart
          }
          onChange={(newFees: any) => {
            const newOffer = updateFeesList(props, newFees);
            props.onSave(newOffer);
          }}
          translations={props.translations}
          closeModal={() => setModalOpen(false)}
          sendAmplitudeEvent={props.sendAmplitudeEvent}
          isMarketscanEnabled={isMarketscanEnabled}
        />
      ) : null;
      break;
    }
    case 'incentives': {
      modal = isMarketScanIncentivesEnabled(props.dealer) ? (
        <EditMarketscanIncentives
          car={props.car}
          financeType={props.offer.data?.financeType!}
          deal={props.deal}
          dealer={props.dealer}
          offers={allOffersForCurrentFinanceType.map(o => o.data as Offer)}
          activeOffer={isModalOpen && (props.offer.data as any)}
          deskingOfferId={props.offer.id}
          deskingId={props.offer.deskingId ?? ''}
          isOnePay={isOnePayLease(props) ?? false}
          onSave={props.onIncentivesChange ?? (() => {})}
          handleClose={() => setModalOpen(false)}
          sendAmplitudeEvent={props.sendAmplitudeEvent}
          disabled={props.readOnlyMode}
        />
      ) : (
        <EditIncentives
          car={props.car}
          financeType={props.offer.data?.financeType!}
          deal={props.deal}
          dealer={props.dealer}
          offers={allOffersForCurrentFinanceType.map(o => o.data as Offer)}
          activeOffer={isModalOpen && (props.offer.data as any)}
          isOnePay={isOnePayLease(props) ?? false}
          onSave={props.onIncentivesChange ?? (() => {})}
          handleClose={() => setModalOpen(false)}
          sendAmplitudeEvent={props.sendAmplitudeEvent}
          disabled={props.readOnlyMode}
        />
      );
      break;
    }
    case 'protectionPlans': {
      modal = (
        <ProtectionPlansModal
          car={props.car}
          deal={props.deal}
          dealer={props.dealer}
          pencil={props.pencil}
          offer={props.offer}
          onChange={props.onSave}
          isOpen={isModalOpen}
          closeModal={() => setModalOpen(false)}
          sendAmplitudeEvent={props.sendAmplitudeEvent}
          disabled={props.readOnlyMode}
        />
      );
      break;
    }
    case 'taxRate': {
      const dealerAddressData = {
        county: props.dealer.county ?? '',
        city: props.dealer.city ?? '',
        state: props.dealer.state ?? '',
      };
      let customerAddressData = {
        county: props.deal.applicant?.county,
        city: props.deal.applicant?.city,
        state: props.deal.applicant?.state,
      };
      if (Object.values(customerAddressData).every(isEmpty)) {
        customerAddressData = {} as any;
      }
      const addressInfo = { customerAddressData, dealerAddressData };
      const updateTaxes = (
        newValue: number,
        taxType: string,
        taxName: string,
        prop: string,
      ) => {
        const newChangeLog = changeLogHelper.updateTaxes(
          { ...props.offer, changeLog },
          taxType,
          taxName,
          prop,
          newValue,
        );
        setChangeLog(newChangeLog ?? {});
      };
      modal = (
        <OfferTaxModal
          isOpen={isModalOpen}
          handleClose={() => {
            setChangeLog(props.offer.changeLog ? props.offer.changeLog : {});
            setModalOpen(false);
          }}
          financeType={props.offer.data?.financeType!}
          taxBreakdown={props.offer.data?.taxBreakdown!}
          onChange={taxBreakdown => {
            const newOffer = cloneDeep(props.offer);
            const newCombinedTaxRate = Object.values(taxBreakdown)
              .map(entry => entry?.rate ?? 0)
              .reduce((prev, curr) => prev + curr, 0);
            set(newOffer, ['data', 'tax'], newCombinedTaxRate);
            set(newOffer, ['data', 'taxBreakdown'], taxBreakdown);
            set(newOffer, 'changeLog', changeLog);
            props.onSave(newOffer);
          }}
          updateTaxes={updateTaxes}
          activeOfferIndex={0 /* TODO: Fix this */}
          pencil={props.pencil}
          dealId={props.deal.id}
          addressInfo={addressInfo}
          dealer={props.dealer}
          sendAmplitudeEvent={props.sendAmplitudeEvent}
          disabled={props.readOnlyMode}
        />
      );
      break;
    }
  }

  return (
    <div className="modal-label">
      <div>
        <a
          className="clickable"
          onClick={() => {
            if (props.sendAmplitudeEvent) {
              props.sendAmplitudeEvent(props.type, Action.CLICK);

              props.sendAmplitudeEvent(props.type, Action.START);
            }
            setModalOpen(true);
          }}
        >
          {props.label}
        </a>
      </div>
      {props.sublabel && <div>{props.sublabel}</div>}
      {modal}
    </div>
  );
};

export default ModalLabel;
